import { type Static, Type } from "@sinclair/typebox"
import { DOMAIN_SLUG_REGEX, USERNAME_REGEX } from "../identifier"
import { Nullable, ShardInfoSchema } from "./common"

// Endpoints públicos de autenticación — docs/04 §2.

// Usuario tal como viaja en la respuesta de login/userinfo.
/**
 * Tenant SECUNDARIO: un dominio que el usuario puede mirar además del suyo.
 *
 * Lleva `node_id` a propósito. Cada dominio vive en UN nodo y preguntarle al
 * equivocado devuelve vacío sin error, así que el cliente necesita el mapa
 * `tenant → nodo`. La otra vía para obtenerlo (`/v1/admin/domains`) exige ser
 * maestro, y estos usuarios no lo son: sin esto verían la lista de tenants y no
 * los datos.
 */
export const SecondaryDomainSchema = Type.Object({
  id: Type.String(),
  slug: Type.String(),
  name: Type.String(),
  node_id: Nullable(Type.String()),
})
export type SecondaryDomain = Static<typeof SecondaryDomainSchema>

export const SessionUserSchema = Type.Object({
  id: Type.String(),
  username: Type.String(),
  identifier: Type.String(),
  email: Nullable(Type.String({ format: "email" })),
  name: Nullable(Type.String()),
  domain: Type.String(),
  // Nombre visible del dominio propio. El slug identifica; esto es lo que se
  // enseña. Sin ello el selector de tenant mezclaba `delmonte` (slug del propio)
  // con `AIMI` (nombre de un secundario) en la misma lista.
  domain_name: Type.String(),
  is_master: Type.Boolean(),
  // Acceso restringido a lo asignado (vehículos, grupos, zonas, personal). El app
  // lo usa solo para pintar: la autoridad es el backend (claim `rst` del token).
  restricted: Type.Boolean(),
  roles: Type.Array(Type.String()),
  scopes: Type.Array(Type.String()),
  // Vacío en el caso normal (un usuario, un tenant).
  secondary_domains: Type.Array(SecondaryDomainSchema),
})
export type SessionUser = Static<typeof SessionUserSchema>

// `identifier` (username.dominio) o `username`+`domain` son alternativas equivalentes.
// `cookie: true` ⇒ el refresh viaja en la cookie rema_rt y se omite del JSON
// (contrato de sesión de la consola). `scope` acota los scopes solicitados [V1].
export const LoginRequestSchema = Type.Object({
  identifier: Type.Optional(Type.String()),
  username: Type.Optional(Type.String({ pattern: USERNAME_REGEX.source })),
  domain: Type.Optional(Type.String({ pattern: DOMAIN_SLUG_REGEX.source })),
  password: Type.String({ minLength: 1 }),
  scope: Type.Optional(Type.Array(Type.String())),
  cookie: Type.Optional(Type.Boolean()),
})
export type LoginRequest = Static<typeof LoginRequestSchema>

export const LoginResponseSchema = Type.Object({
  token_type: Type.Literal("Bearer"),
  access_token: Type.String(),
  expires_in: Type.Integer(),
  // Omitido cuando la sesión usa cookie rema_rt
  refresh_token: Type.Optional(Type.String()),
  user: SessionUserSchema,
  // null mientras la instancia no tenga nodos registrados
  shard: Nullable(ShardInfoSchema),
})
export type LoginResponse = Static<typeof LoginResponseSchema>

// Si el usuario tiene MFA activo [V1], login devuelve 200 con mfa_token (TTL 5 min).
export const MfaRequiredResponseSchema = Type.Object({
  mfa_required: Type.Literal(true),
  mfa_token: Type.String(),
})
export type MfaRequiredResponse = Static<typeof MfaRequiredResponseSchema>

export const LoginResultSchema = Type.Union([LoginResponseSchema, MfaRequiredResponseSchema])
export type LoginResult = Static<typeof LoginResultSchema>

// TOTP de 6 dígitos o código de recuperación; responde el payload completo de login.
export const MfaVerifyRequestSchema = Type.Object({
  mfa_token: Type.String(),
  code: Type.String({ minLength: 6, maxLength: 16 }),
})
export type MfaVerifyRequest = Static<typeof MfaVerifyRequestSchema>

// Si falta refresh_token, el servidor usa la cookie rema_rt; rota y re-emite cookie.
export const RefreshRequestSchema = Type.Object({
  refresh_token: Type.Optional(Type.String()),
  scope: Type.Optional(Type.Array(Type.String())),
})
export type RefreshRequest = Static<typeof RefreshRequestSchema>

// Revoca la familia de la sesión (token del body o cookie) y limpia la cookie.
export const LogoutRequestSchema = Type.Object({
  refresh_token: Type.Optional(Type.String()),
})
export type LogoutRequest = Static<typeof LogoutRequestSchema>

// Perfil + scopes + shard del token presentado.
export const UserinfoResponseSchema = Type.Composite([
  SessionUserSchema,
  Type.Object({ shard: Nullable(ShardInfoSchema) }),
])
export type UserinfoResponse = Static<typeof UserinfoResponseSchema>

// Registro self-service: solo opera si el dominio lo habilita (403 registration_disabled).
// email obligatorio solo si la política del dominio lo exige.
export const RegisterRequestSchema = Type.Object({
  identifier: Type.Optional(Type.String()),
  username: Type.Optional(Type.String({ pattern: USERNAME_REGEX.source })),
  domain: Type.Optional(Type.String({ pattern: DOMAIN_SLUG_REGEX.source })),
  password: Type.String({ minLength: 1 }),
  name: Type.Optional(Type.String()),
  email: Type.Optional(Type.String({ format: "email" })),
})
export type RegisterRequest = Static<typeof RegisterRequestSchema>

// 201 sin tokens cuando el dominio exige verificación de email [V1].
export const VerificationRequiredResponseSchema = Type.Object({
  verification_required: Type.Literal(true),
})
export type VerificationRequiredResponse = Static<typeof VerificationRequiredResponseSchema>

export const RegisterResponseSchema = Type.Union([
  LoginResponseSchema,
  VerificationRequiredResponseSchema,
])
export type RegisterResponse = Static<typeof RegisterResponseSchema>

// Responde siempre 200 exista o no el identificador (anti-enumeración, docs/06 §1).
export const ForgotPasswordRequestSchema = Type.Object({
  identifier: Type.String(),
})
export type ForgotPasswordRequest = Static<typeof ForgotPasswordRequestSchema>

export const ResetPasswordRequestSchema = Type.Object({
  // Token opaco de un solo uso recibido por email
  token: Type.String(),
  password: Type.String({ minLength: 1 }),
})
export type ResetPasswordRequest = Static<typeof ResetPasswordRequestSchema>
